
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
type ValidateFieldCallback = (message?: string | Error) => void;
import {
  checkKaisha,
  createKaisha,
  getGyoushuList,
  getKaishaById,
  updateKaisha,
} from "@/api/kaisha.api";
import { KaishaModel } from "@/interfaces/kaisha.interface";
import { getUnyoList } from "@/api/masterTable.api";
import { dateFormat } from "@/utils/date";

export default defineComponent({
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    const form = reactive({
      id: 0,
      kaishaCd: "",
      kaishaNm: "",
      unyoCd: "",
      gyoushuCd: "",
      mukoDate: "",
      mukoFlg: false,
    } as KaishaModel);
    const formRef = ref();
    const loading = ref(false);
    //入力情報のチェックルール
    const rules = {
      kaishaCd: [
        {
          required: true,
          validator: async (
            self: any,
            value: string,
            callback: ValidateFieldCallback
          ) => {
            if (!value) {
              callback(CheckMsg.CHK_KAISHA_CD);
            } else if (value.length < 6) {
              callback(CheckMsg.CHK_KAISHA_CD_LENGTH);
            } else {
              try {
                await checkKaisha({
                  id: rowId.value,
                  databaseField: "kaisha_cd",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "会社コード",
        },
      ],
      kaishaNm: [
        {
          required: true,
          trigger: "blur",
          message: CheckMsg.CHK_KAISHA_NM,
        },
      ],
      unyoCd: [
        {
          required: true,
          message: CheckMsg.CHK_UNYO,
          trigger: "blur",
        },
      ],
      gyoushuCd: [
        {
          required: true,
          message: CheckMsg.CHK_GYOUSHU,
          trigger: "blur",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      //select inputのみ手動で値を消す
      form.unyoCd = "";
      form.gyoushuCd = "";
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            if (props.operation === DialogGenre.Add) {
              await createKaisha({
                kaishaCd: form.kaishaCd,
                kaishaNm: form.kaishaNm,
                unyoCd: form.unyoCd,
                gyoushuCd: form.gyoushuCd,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_KAISHA_ADDED,
              });
            } else {
              await updateKaisha(form.id, {
                kaishaNm: form.kaishaNm,
                unyoCd: form.unyoCd,
                gyoushuCd: form.gyoushuCd,
                mukoFlg: form.mukoFlg,
              });
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_KAISHA_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getkaishaInfo = async (id: number) => {
      const {
        data: { result },
      } = await getKaishaById(id);
      form.id = result.id;
      form.kaishaCd = result.kaisha_cd;
      form.kaishaNm = result.kaisha_nm;
      form.unyoCd = result.unyo.unyo_kubun_cd;
      form.gyoushuCd = result.gyoushu.gyoushu_bunrui_cd;
      form.mukoFlg = result.muko_flg;
      form.mukoDate = result.muko_flg ? dateFormat(result.muko_date) : "";
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getkaishaInfo(rowId.value as number);
      }
    });

    //運用区分と業種分類プルダウン初期化
    const unyoList = await getUnyoList();
    const {
      data: { result },
    } = await getGyoushuList();
    const gyoushuList = result.listData;

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
      unyoList,
      gyoushuList,
    };
  },
});
